import React, { useState, useMemo, useRef, useCallback } from "react";
import Hero from "../../components/Hero/Hero";
import PageFooter from "../../components/PageFooter/PageFooter";
import "./gbpCategories.css";
import Seo from "../../i18n/Seo";
import useLang from "../../i18n/useLang";
import content from "./content";

import frData from "./data/fr.json";
import enUSData from "./data/en-US.json";
import enGBData from "./data/en-GB.json";
import esData from "./data/es.json";
import itData from "./data/it.json";

const LANGUAGES = [
  { key: "fr", flag: "🇫🇷", data: frData },
  { key: "en-US", flag: "🇺🇸", data: enUSData },
  { key: "en-GB", flag: "🇬🇧", data: enGBData },
  { key: "es", flag: "🇪🇸", data: esData },
  { key: "it", flag: "🇮🇹", data: itData },
];

const SCALE_ZONES = ["bad", "good", "ok", "sus"];

const PAGE_SIZE = 50;

const GbpCategories = () => {
  const lang = useLang();
  const t = content[lang];
  const [search, setSearch] = useState("");
  const [selectedLang, setSelectedLang] = useState(t.defaultDataset);
  const [copiedId, setCopiedId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const toolRef = useRef(null);

  const scrollToTool = () => {
    toolRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const currentLang = LANGUAGES.find((l) => l.key === selectedLang);
  const categories = currentLang?.data || [];

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return categories;
    return categories.filter(
      (c) =>
        c.category.toLowerCase().includes(q) ||
        c.gcid.toLowerCase().includes(q),
    );
  }, [search, categories]);

  const displayed = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount],
  );

  const handleSearch = useCallback((e) => {
    setSearch(e.target.value);
    setVisibleCount(PAGE_SIZE);
  }, []);

  const handleCopy = useCallback(async (text, gcid) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(gcid);
      setTimeout(() => setCopiedId(null), 1800);
    } catch {
      /* fallback silently */
    }
  }, []);

  const loadMore = () => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  };

  return (
    <div className="gbp-page">
      <Seo
        routeId="gbpCategories"
        title={t.seo.title}
        description={t.seo.description}
        ogDescription={t.seo.ogDescription}
      />

      <Hero
        title={t.hero.title}
        subtitle={t.hero.subtitle}
        ctaText={t.hero.cta}
        ctaOnClick={scrollToTool}
      />

      <div className="gbp" ref={toolRef}>
        <div className="gbp__card">
          <div className="gbp__controls">
            <div className="gbp__search-wrap">
              <svg
                className="gbp__search-icon"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z"
                  stroke="currentColor"
                  strokeWidth="1.66"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <input
                className="gbp__search"
                type="text"
                placeholder={t.tool.searchPlaceholder}
                value={search}
                onChange={handleSearch}
                autoFocus
              />
              {search && (
                <button
                  className="gbp__search-clear"
                  onClick={() => {
                    setSearch("");
                    setVisibleCount(PAGE_SIZE);
                  }}
                  aria-label={t.tool.clearSearch}
                >
                  ×
                </button>
              )}
            </div>

            <div className="gbp__lang-row">
              {LANGUAGES.map((dataset) => (
                <button
                  key={dataset.key}
                  className={`gbp__lang${
                    selectedLang === dataset.key ? " gbp__lang--active" : ""
                  }`}
                  onClick={() => {
                    setSelectedLang(dataset.key);
                    setVisibleCount(PAGE_SIZE);
                  }}
                >
                  <span className="gbp__lang-flag">{dataset.flag}</span>
                  <span className="gbp__lang-label">
                    {t.tool.languages[dataset.key]}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="gbp__meta">
            <span className="gbp__count">
              {t.tool.count(filtered.length, Boolean(search))}
            </span>
          </div>

          <div className="gbp__list">
            <div className="gbp__list-header">
              <span className="gbp__col-name">{t.tool.colCategory}</span>
              <span className="gbp__col-id">{t.tool.colId}</span>
              <span className="gbp__col-action"></span>
            </div>
            {displayed.length === 0 && (
              <div className="gbp__empty">
                <span className="gbp__empty-icon">🔍</span>
                <p>{t.tool.empty(search)}</p>
                <p className="gbp__empty-hint">{t.tool.emptyHint}</p>
              </div>
            )}
            {displayed.map((cat) => (
              <div className="gbp__row" key={cat.gcid}>
                <span className="gbp__col-name" title={cat.category}>
                  {cat.category}
                </span>
                <span className="gbp__col-id">
                  <code>{cat.gcid}</code>
                </span>
                <span className="gbp__col-action">
                  <button
                    className={`gbp__copy${
                      copiedId === cat.gcid ? " gbp__copy--done" : ""
                    }`}
                    onClick={() => handleCopy(cat.category, cat.gcid)}
                    title={t.tool.copyTitle}
                  >
                    {copiedId === cat.gcid ? (
                      <svg
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M16.6666 5L7.49998 14.1667L3.33331 10"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="7"
                          y="7"
                          width="10"
                          height="10"
                          rx="2"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M13 7V5C13 3.89543 12.1046 3 11 3H5C3.89543 3 3 3.89543 3 5V11C3 12.1046 3.89543 13 5 13H7"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                      </svg>
                    )}
                    <span>
                      {copiedId === cat.gcid ? t.tool.copied : t.tool.copy}
                    </span>
                  </button>
                </span>
              </div>
            ))}
          </div>

          {visibleCount < filtered.length && (
            <div className="gbp__load-more">
              <button className="gbp__load-more-btn" onClick={loadMore}>
                {t.tool.loadMore(filtered.length - visibleCount)}
              </button>
            </div>
          )}
        </div>
      </div>

      <section className="gbp-seo">
        <div className="gbp-seo__inner">
          <article className="gbp-seo__block">
            <h2>{t.intro.title}</h2>
            <p>{t.intro.text}</p>
            <div className="gbp-seo__stats-row">
              <div className="gbp-seo__stat-card">
                <span className="gbp-seo__stat-number">
                  {categories.length.toLocaleString(t.locale)}+
                </span>
                <span className="gbp-seo__stat-text">
                  {t.intro.statCategories}
                </span>
              </div>
              <div className="gbp-seo__stat-card">
                <span className="gbp-seo__stat-number">5</span>
                <span className="gbp-seo__stat-text">
                  {t.intro.statLanguages}
                </span>
              </div>
              <div className="gbp-seo__stat-card">
                <span className="gbp-seo__stat-number">10</span>
                <span className="gbp-seo__stat-text">{t.intro.statMax}</span>
              </div>
            </div>
          </article>

          <article className="gbp-seo__block">
            <h2>{t.why.title}</h2>
            {t.why.paragraphs.map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
          </article>

          <article className="gbp-seo__block">
            <h2>{t.tips.title}</h2>
            <div className="gbp-seo__tips">
              {t.tips.items.map((tip) => (
                <div className="gbp-seo__tip" key={tip.icon}>
                  <span className="gbp-seo__tip-icon">{tip.icon}</span>
                  <div>
                    <strong>{tip.title}</strong>
                    <p>{tip.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </article>

          <article className="gbp-seo__block">
            <h2>{t.howMany.title}</h2>
            <div className="gbp-seo__scale">
              {SCALE_ZONES.map((zone) => (
                <div
                  className={`gbp-seo__scale-zone gbp-seo__scale-zone--${zone}`}
                  key={zone}
                >
                  <span className="gbp-seo__scale-range">
                    {t.howMany.scale[zone].range}
                  </span>
                  <span className="gbp-seo__scale-label">
                    {t.howMany.scale[zone].label}
                  </span>
                </div>
              ))}
            </div>
            <p>{t.howMany.text}</p>
          </article>

          <article className="gbp-seo__block">
            <h2>{t.impact.title}</h2>
            <div className="gbp-seo__factors">
              {t.impact.factors.map((factor) => (
                <div className="gbp-seo__factor" key={factor.icon}>
                  <span className="gbp-seo__factor-icon">{factor.icon}</span>
                  <strong>{factor.title}</strong>
                  <p>{factor.text}</p>
                </div>
              ))}
            </div>
            <p>{t.impact.text}</p>
          </article>

          <article className="gbp-seo__block">
            <h2>{t.faq.title}</h2>
            <div className="gbp-seo__faq">
              {t.faq.items.map((item) => (
                <div className="gbp-seo__faq-item" key={item.q}>
                  <h3>{item.q}</h3>
                  <p>{item.a}</p>
                </div>
              ))}
            </div>
          </article>
        </div>
      </section>

      <PageFooter />
    </div>
  );
};

export default GbpCategories;
